/**
 * inventoryMovementLedger.ts
 *
 * Append-Only Inventory Movement Ledger.
 * Enforces conservation of physical mass across multi-warehouse locations,
 * atomic dual-legged stock transfers, and kernel capability checks.
 */

import type {
  InventoryMovement,
  StockProjection,
  BatchStock,
  TransferOrder,
  WarehouseLocation,
  ProductAllergenProfile,
} from './inventoryContracts.js';
import { requireCapability } from '../../../kernel/authorization/requireCapability.js';
import type { AuthorizationContext } from '../../../kernel/authorization/capabilities.js';

import { SECURITY_TACTICAL_CATALOG } from '../security-tactical-catalog.js';

export class InventoryMovementLedger {
  private movements: InventoryMovement[] = [];
  private locations: Map<string, WarehouseLocation> = new Map();
  private productNames: Map<string, string> = new Map();

  constructor() {
    // Default Warehouses
    this.registerLocation({
      locationId: 'loc-main',
      name: 'Central Tactical Armory & Warehouse',
      code: 'WH-CENTRAL',
      isFulfillmentNode: true,
      isRetailCounter: false,
    });
    this.registerLocation({
      locationId: 'loc-counter',
      name: 'Frontline Security Counter',
      code: 'POS-FRONT',
      isFulfillmentNode: false,
      isRetailCounter: true,
    });
    this.registerLocation({
      locationId: 'loc-warehouse-1',
      name: 'Forward Defense Depot North',
      code: 'DEPOT-NORTH',
      isFulfillmentNode: true,
      isRetailCounter: false,
    });

    // Populate Authoritative Security & Tactical Products
    for (const item of SECURITY_TACTICAL_CATALOG) {
      this.productNames.set(item.sku, item.name);
    }

    // Seed initial stock
    this.seedInitialMovements();
  }

  registerLocation(location: WarehouseLocation): void {
    this.locations.set(location.locationId, location);
  }

  getLocations(): readonly WarehouseLocation[] {
    return Array.from(this.locations.values());
  }

  setProductName(sku: string, name: string): void {
    this.productNames.set(sku, name);
  }

  /**
   * Append a single verified movement.
   */
  appendMovement(movement: InventoryMovement): void {
    this.movements.push(movement);
  }

  /**
   * Receive stock into a specific location.
   */
  receiveStock(
    context: AuthorizationContext,
    params: {
      sku: string;
      locationId: string;
      lotNumber: string;
      expiryDate?: string;
      quantity: number;
      reason?: string;
      referenceId?: string;
    }
  ): InventoryMovement {
    requireCapability('inventory.receive', context, { sku: params.sku, locationId: params.locationId });

    if (params.quantity <= 0) {
      throw new Error('[InventoryLedger] Received quantity must be positive');
    }
    if (!this.locations.has(params.locationId)) {
      throw new Error(`[InventoryLedger] Invalid location ID: ${params.locationId}`);
    }

    const movement: InventoryMovement = {
      movementId: `mov_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      sku: params.sku,
      locationId: params.locationId,
      lotNumber: params.lotNumber,
      expiryDate: params.expiryDate,
      quantityDelta: params.quantity,
      movementType: 'receipt',
      referenceId: params.referenceId || `po_${Date.now()}`,
      performedBy: context.principal.id,
      reason: params.reason || 'Standard Stock Receipt',
      occurredAt: new Date().toISOString(),
    };

    this.appendMovement(movement);
    return movement;
  }

  /**
   * Atomic Dual-Legged Location Transfer.
   * Invariant: Sum of delta across all locations = 0.
   */
  transferStock(
    context: AuthorizationContext,
    order: TransferOrder
  ): { transferOut: InventoryMovement; transferIn: InventoryMovement } {
    requireCapability('inventory.transfer', context, {
      sku: order.sku,
      sourceLocationId: order.sourceLocationId,
      targetLocationId: order.targetLocationId,
    });

    if (order.quantity <= 0) {
      throw new Error('[InventoryLedger] Transfer quantity must be positive');
    }
    if (order.sourceLocationId === order.targetLocationId) {
      throw new Error('[InventoryLedger] Source and target locations must be distinct');
    }
    if (!this.locations.has(order.sourceLocationId) || !this.locations.has(order.targetLocationId)) {
      throw new Error('[InventoryLedger] One or more invalid location IDs');
    }

    // Verify source stock availability
    const sourceProjection = this.getStockProjection(order.sku, order.sourceLocationId);
    if (sourceProjection.availableToSell < order.quantity) {
      throw new Error(
        `[InventoryLedger] Insufficient stock at ${order.sourceLocationId}. Available: ${sourceProjection.availableToSell}, Requested: ${order.quantity}`
      );
    }

    const transferRefId = order.transferId || `xfer_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const timestamp = new Date().toISOString();

    const transferOut: InventoryMovement = {
      movementId: `mov_out_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      sku: order.sku,
      locationId: order.sourceLocationId,
      lotNumber: order.lotNumber,
      expiryDate: order.expiryDate,
      quantityDelta: -order.quantity,
      movementType: 'transfer-out',
      referenceId: transferRefId,
      performedBy: context.principal.id,
      reason: order.reason || `Transfer to ${order.targetLocationId}`,
      occurredAt: timestamp,
    };

    const transferIn: InventoryMovement = {
      movementId: `mov_in_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      sku: order.sku,
      locationId: order.targetLocationId,
      lotNumber: order.lotNumber,
      expiryDate: order.expiryDate,
      quantityDelta: order.quantity,
      movementType: 'transfer-in',
      referenceId: transferRefId,
      performedBy: context.principal.id,
      reason: order.reason || `Transfer from ${order.sourceLocationId}`,
      occurredAt: timestamp,
    };

    // Atomic append
    this.appendMovement(transferOut);
    this.appendMovement(transferIn);

    return { transferOut, transferIn };
  }

  /**
   * Adjust or Write-off inventory.
   */
  adjustStock(
    context: AuthorizationContext,
    params: {
      sku: string;
      locationId: string;
      lotNumber: string;
      expiryDate?: string;
      quantityDelta: number;
      type: 'adjustment' | 'writeoff';
      reason: string;
    }
  ): InventoryMovement {
    const requiredCap = params.type === 'writeoff' ? 'inventory.writeoff' : 'inventory.adjust';
    requireCapability(requiredCap, context, { sku: params.sku, locationId: params.locationId });

    if (!params.reason) {
      throw new Error(`[InventoryLedger] A valid reason is mandatory for ${params.type}`);
    }

    const movement: InventoryMovement = {
      movementId: `mov_adj_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      sku: params.sku,
      locationId: params.locationId,
      lotNumber: params.lotNumber,
      expiryDate: params.expiryDate,
      quantityDelta: params.quantityDelta,
      movementType: params.type,
      referenceId: `adj_${Date.now()}`,
      performedBy: context.principal.id,
      reason: params.reason,
      occurredAt: new Date().toISOString(),
    };

    this.appendMovement(movement);
    return movement;
  }

  /**
   * Directly records an authorized movement into the append-only ledger.
   */
  recordMovement(context: AuthorizationContext, movement: InventoryMovement): void {
    if (!context.principal.authenticated) {
      throw new Error('[InventoryLedger] Unauthenticated movement attempt rejected');
    }
    this.appendMovement(movement);
  }

  /**
   * Derive stock projection for a specific SKU and Location.
   */
  getStockProjection(sku: string, locationId: string): StockProjection {
    const relevant = this.movements.filter(
      (m) => m.sku === sku && (locationId === 'all' || m.locationId === locationId)
    );

    const batchMap = new Map<string, {
      onHand: number;
      reserved: number;
      quarantined: number;
      expiryDate?: string;
      receivedAt: string;
      evidenceMediaIds?: string[];
    }>();
    let damagedOrWrittenOff = 0;

    for (const m of relevant) {
      const batchKey = `${m.sku}_${m.locationId}_${m.lotNumber}`;
      if (!batchMap.has(batchKey)) {
        batchMap.set(batchKey, {
          onHand: 0,
          reserved: 0,
          quarantined: 0,
          expiryDate: m.expiryDate,
          receivedAt: m.occurredAt,
          evidenceMediaIds: [],
        });
      }
      const b = batchMap.get(batchKey)!;

      if (m.movementType === 'reservation') {
        b.reserved += Math.abs(m.quantityDelta);
      } else if (m.movementType === 'release') {
        b.reserved = Math.max(0, b.reserved - Math.abs(m.quantityDelta));
      } else if (m.movementType === 'quarantine-hold') {
        b.quarantined += Math.abs(m.quantityDelta);
      } else if (m.movementType === 'quarantine-release') {
        b.quarantined = Math.max(0, b.quarantined - Math.abs(m.quantityDelta));
      } else if (m.movementType === 'quarantine-writeoff') {
        b.quarantined = Math.max(0, b.quarantined - Math.abs(m.quantityDelta));
        b.onHand = Math.max(0, b.onHand - Math.abs(m.quantityDelta));
        damagedOrWrittenOff += Math.abs(m.quantityDelta);
      } else if (m.movementType === 'writeoff') {
        b.onHand += m.quantityDelta; // Negative delta
        damagedOrWrittenOff += Math.abs(m.quantityDelta);
      } else {
        b.onHand += m.quantityDelta;
      }

      if (m.evidenceMediaIds && m.evidenceMediaIds.length > 0) {
        b.evidenceMediaIds = Array.from(new Set([...(b.evidenceMediaIds || []), ...m.evidenceMediaIds]));
      }
    }

    const batches: BatchStock[] = [];
    let totalOnHand = 0;
    let totalReserved = 0;
    let totalQuarantined = 0;
    const activeExpiryDates: string[] = [];

    for (const [key, b] of batchMap.entries()) {
      const parts = key.split('_');
      const lotNumber = parts.slice(2).join('_');
      const avail = Math.max(0, b.onHand - b.reserved - b.quarantined);
      totalOnHand += b.onHand;
      totalReserved += b.reserved;
      totalQuarantined += b.quarantined;

      if (b.expiryDate && b.onHand > 0) {
        activeExpiryDates.push(b.expiryDate);
      }

      const operationalStatus = b.quarantined >= b.onHand && b.onHand > 0 ? 'quarantined' : 'available';

      batches.push({
        sku,
        locationId,
        lotNumber,
        expiryDate: b.expiryDate,
        receivedAt: b.receivedAt,
        onHand: b.onHand,
        reserved: b.reserved,
        quarantined: b.quarantined,
        availableToSell: avail,
        operationalStatus,
        evidenceMediaIds: b.evidenceMediaIds,
      });
    }

    activeExpiryDates.sort();
    const nearestExpiryDate = activeExpiryDates[0];
    const availableToSell = Math.max(0, totalOnHand - totalReserved - totalQuarantined);
    const productName = this.productNames.get(sku) || sku;

    // Seed sample 3-state allergen profile
    const allergenProfile: ProductAllergenProfile = {
      status: 'certified',
      reviewedByPrincipalId: 'sovereign-owner',
      reviewedAt: new Date().toISOString(),
      allergens: {
        gluten: sku.includes('CROISS') ? 'contains' : 'free_of',
        dairy: sku.includes('CROISS') || sku.includes('MATCHA') ? 'may_contain_cross_contact' : 'free_of',
        eggs: 'free_of',
        peanuts: 'free_of',
        treeNuts: 'free_of',
        soy: 'free_of',
        fish: 'free_of',
        crustaceans: 'free_of',
        sesame: 'free_of',
      },
    };

    return {
      sku,
      productName,
      locationId,
      onHand: totalOnHand,
      reserved: totalReserved,
      quarantined: totalQuarantined,
      damagedOrWrittenOff,
      availableToSell,
      batches,
      lowStockThreshold: 5,
      allergenProfile,
      nearestExpiryDate,
      mediaIds: [`med_product_${sku.toLowerCase().replace(/[^a-z0-9]/g, '_')}`],
    };
  }

  /**
   * Get stock projections across all SKUs.
   */
  getAllStockProjections(locationId: string = 'all'): readonly StockProjection[] {
    const skus = Array.from(new Set(this.movements.map((m) => m.sku)));
    return skus.map((sku) => this.getStockProjection(sku, locationId));
  }

  getAllMovements(): readonly InventoryMovement[] {
    return [...this.movements];
  }

  getMovements(): readonly InventoryMovement[] {
    return [...this.movements];
  }

  private seedInitialMovements(): void {
    const now = Date.now();
    const day = 86_400_000;

    // Seed stock for all 15 items in Central Armory & Retail Counter
    SECURITY_TACTICAL_CATALOG.forEach((item, index) => {
      // Main Warehouse Fulfillment Stock
      this.movements.push({
        movementId: `init_main_${index + 1}`,
        sku: item.sku,
        locationId: 'loc-main',
        lotNumber: `LOT-SEC-${2026}-A${index + 1}`,
        expiryDate: new Date(now + (365 + index * 30) * day).toISOString().slice(0, 10),
        quantityDelta: 20 + (index % 5) * 10,
        movementType: 'receipt',
        performedBy: 'sovereign-owner',
        reason: 'Initial Armory Staging',
        occurredAt: new Date(now - 7 * day).toISOString(),
      });

      // Frontline Retail Counter Stock
      this.movements.push({
        movementId: `init_counter_${index + 1}`,
        sku: item.sku,
        locationId: 'loc-counter',
        lotNumber: `LOT-SEC-${2026}-A${index + 1}`,
        expiryDate: new Date(now + (365 + index * 30) * day).toISOString().slice(0, 10),
        quantityDelta: 5 + (index % 3) * 2,
        movementType: 'receipt',
        performedBy: 'sovereign-owner',
        reason: 'Frontline Display Staging',
        occurredAt: new Date(now - 3 * day).toISOString(),
      });
    });
  }
}

export const globalInventoryMovementLedger = new InventoryMovementLedger();
